import { cn } from "@/lib/utils";
import { WIDTH_MD } from "@constants/global";
import { ESystemRoles } from "@constants/SystemRoles";
import { ICONS_MODULES_PATHS } from "components/Layout/Sidebar/components/body/content/utils/iconModulesPath";
import SVG from "components/SVG";
import { ExploreCardFrame, LiquidPill, LiquidPillGroup } from "components/ui/v2";
import type { CircleActionVariant } from "components/ui/v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import useTranslation from "next-translate/useTranslation";
import { X } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { EMessageTabs } from "types/Message.type";
import { IUser } from "types/User.types";
import useWindowSize from "utils/hooks/useWindowSize";
import NewMessageBenefit from "./NewMessageBenefit";
import NewMessageCapsule from "./NewMessageCapsule";
import NewMessageMagazine from "./NewMessageMagazine";
import NewMessageText from "./NewMessageText";
import TabRecognition from "./TabRecognition/TabRecognition";


const TAB_CFG: Array<{
  value: string;
  label: string;
  icon: string;
  color: string;
  glow: string;
  auroBg: string;
  variant: CircleActionVariant;
}> = [
  {
    value: EMessageTabs.TEXT.toLowerCase(),
    label: "Mensaje",
    icon: ICONS_MODULES_PATHS.EXPLORE.MESSAGE,
    color: "#6366f1",
    glow: "rgba(99,102,241,0.4)",
    auroBg: "radial-gradient(circle, rgba(99,102,241,0.32) 0%, rgba(139,92,246,0.1) 55%, transparent 75%)",
    variant: "indigo",
  },
  {
    value: EMessageTabs.CAPSULES.toLowerCase(),
    label: "Cápsulas",
    icon: ICONS_MODULES_PATHS.EXPLORE.CAPSULES,
    color: "#8b5cf6",
    glow: "rgba(139,92,246,0.4)",
    auroBg: "radial-gradient(circle, rgba(139,92,246,0.32) 0%, rgba(167,139,250,0.1) 55%, transparent 75%)",
    variant: "violet",
  },
  {
    value: EMessageTabs.RECOGNITION.toLowerCase(),
    label: "Reconocimiento",
    icon: ICONS_MODULES_PATHS.EXPLORE.RECOGNITION,
    color: "#f59e0b",
    glow: "rgba(245,158,11,0.4)",
    auroBg: "radial-gradient(circle, rgba(245,158,11,0.32) 0%, rgba(251,146,60,0.1) 55%, transparent 75%)",
    variant: "gold",
  },
  {
    value: EMessageTabs.MAGAZINE.toLowerCase(),
    label: "Canva",
    icon: ICONS_MODULES_PATHS.EXPLORE.CANVA,
    color: "#0ea5e9",
    glow: "rgba(14,165,233,0.4)",
    auroBg: "radial-gradient(circle, rgba(14,165,233,0.32) 0%, rgba(34,211,238,0.1) 55%, transparent 75%)",
    variant: "sky",
  },
  {
    value: "benefit",
    label: "Beneficio",
    icon: ICONS_MODULES_PATHS.BENEFITS.BENEFITS_MODULE,
    color: "#8b5cf6",
    glow: "rgba(139,92,246,0.4)",
    auroBg: "radial-gradient(circle, rgba(139,92,246,0.32) 0%, rgba(167,139,250,0.1) 55%, transparent 75%)",
    variant: "violet",
  },
];

interface IProps {
  usersToMention: IUser[];
  usersLoading: boolean;
  /** @deprecated kept for API compat — form now expands inline */
  handleNewMessage?: (tab?: keyof typeof EMessageTabs) => void;
  noteOpen?: boolean;
  setNoteOpen?: (value: boolean) => void;
}

const NewMessage = memo(function NewMessage({ usersLoading, usersToMention }: IProps) {
  const { vendor } = useVendor();
  const { user } = useUserStoreContext((store) => ({ user: store.user }));
  const isAdmin = user?.isi || user?.systemRole === ESystemRoles.ADMIN;
  const { t } = useTranslation("explore");
  const wWidth = useWindowSize()?.width;
  const isSm = wWidth < WIDTH_MD;

  const containerRef = useRef<HTMLDivElement>(null);
  // isExpanded drives the CSS grid animation; isPanelVisible keeps DOM alive during close
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(EMessageTabs.TEXT.toLowerCase());

  const visibleTabs = TAB_CFG.filter((tab) => {
    if (tab.value === EMessageTabs.RECOGNITION.toLowerCase()) {
      return vendor?.enableCorporateValue ?? false;
    }
    if (tab.value === "benefit") {
      return isAdmin;
    }
    return true;
  });

  const activeCfg = visibleTabs.find((tab) => tab.value === activeTab) ?? visibleTabs[0];

  const close = useCallback(() => {
    setIsExpanded(false);
    setTimeout(() => setIsPanelVisible(false), 400);
  }, []);

  const open = useCallback((tabValue?: string) => {
    if (usersLoading) return;
    if (tabValue) setActiveTab(tabValue);
    setIsPanelVisible(true);
    // Two rAFs so the DOM is present before the grid transition starts
    requestAnimationFrame(() => requestAnimationFrame(() => setIsExpanded(true)));
  }, [usersLoading]);

  const toggleOpen = useCallback(() => {
    if (usersLoading) return;
    if (isExpanded) {
      close();
      return;
    }
    open();
  }, [usersLoading, isExpanded, close, open]);

  const resetMessageInput = useCallback(() => close(), [close]);

  // Close on click outside (ignora portales: menús, menciones, popovers)
  useEffect(() => {
    if (!isExpanded) return;

    const floatingSelector = [
      "[data-compose-floating]",
      "[data-radix-popper-content-wrapper]",
      "[data-radix-menu-content]",
      "[role='menu']",
      "[role='listbox']",
      "[role='dialog']",
      ".mentions-portal",
      ".rs-popover",
      ".rs-picker-popup",
      ".rs-anim-fade",
    ].join(",");

    const isInsideComposer = (event: MouseEvent) => {
      const root = containerRef.current;
      if (!root) return false;

      for (const node of event.composedPath()) {
        if (!(node instanceof Node)) continue;
        if (root.contains(node)) return true;
        if (
          node instanceof Element &&
          (node.hasAttribute("data-compose-floating") ||
            node.closest(floatingSelector))
        ) {
          return true;
        }
      }
      return false;
    };

    const handleOutside = (event: MouseEvent) => {
      if (!isInsideComposer(event)) close();
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isExpanded, close]);

  return (
    <ExploreCardFrame padding="none">
      <div
        ref={containerRef}
        className={cn("relative", isExpanded ? "overflow-visible" : "overflow-hidden")}
      >
        {/* ── Aurora ───────────────────────────────────────────── */}
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div
            className="absolute -left-8 -top-8 rounded-full blur-2xl transition-all duration-700"
            style={{
              width: isExpanded ? 140 : 80,
              height: isExpanded ? 140 : 80,
              background: "radial-gradient(circle, rgba(34,211,238,0.10) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -right-8 -top-8 rounded-full blur-2xl transition-all duration-700"
            style={{
              width: isExpanded ? 160 : 80,
              height: isExpanded ? 160 : 80,
              background: activeCfg.auroBg,
              opacity: isExpanded ? 0.35 : 0.2,
            }}
          />
          <div
            className="absolute left-1/3 rounded-full blur-2xl transition-all duration-500"
            style={{ top: -12, width: 100, height: 60, background: "radial-gradient(circle, rgba(167,139,250,0.08) 0%, transparent 70%)" }}
          />
          <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-white via-white/80 to-transparent dark:from-zinc-900 dark:via-zinc-900/80 dark:to-transparent" />
        </div>

        {/* ── Contenido (padding interno; card sin padding) ─────── */}
        <div className="relative z-10 p-2">
        {/* ── Trigger row ──────────────────────────────────────── */}
        <div className="flex items-center gap-2 py-0.5 sm:gap-2.5">
          <button
            type="button"
            onClick={toggleOpen}
            disabled={usersLoading}
            className="min-w-0 flex-1 cursor-pointer text-center disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="block truncate text-[15px] font-medium text-slate-500 dark:text-zinc-400">
              {t("¿Quieres compartir algo?")}
            </span>
          </button>

          <LiquidPillGroup
            activeValue={activeTab}
            items={visibleTabs.map((tab) => ({
              value: tab.value,
              label: !isSm ? t(tab.label) : undefined,
              icon: <SVG src={tab.icon} width={14} height={14} />,
              variant: tab.variant,
              disabled: usersLoading,
            }))}
            onChange={(value) => {
              if (isExpanded) setActiveTab(value);
              else open(value);
            }}
          />

          <div className="flex items-center">
            {isExpanded && (
              <LiquidPill
                as="button"
                type="button"
                appearance="normal"
                variant="rose"
                shape="circle"
                size="md"
                onClick={close}
                aria-label={t("Cerrar")}
                icon={<X size={18} strokeWidth={1.5} aria-hidden />}
                className="motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:fade-in-0 motion-safe:duration-200"
              />
            )}
          </div>
        </div>

        {/* ── Expanded panel — height-animated via CSS grid ────── */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: isExpanded ? "1fr" : "0fr",
            transition: "grid-template-rows 400ms cubic-bezier(0.34,1.2,0.64,1)",
          }}
        >
          <div className={isExpanded ? "overflow-visible" : "overflow-hidden"}>
            {isPanelVisible && (
              <div data-compose-panel className="overflow-visible">
                {/* Accent separator */}
                <div
                  aria-hidden
                  className="mt-2 h-px sm:mt-2.5"
                  style={{ background: `linear-gradient(to right, transparent, ${activeCfg.glow}, transparent)` }}
                />

                {/* Form — keyed so it re-animates on tab switch */}
                <div
                  key={activeTab}
                  className="relative motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-200"
                >
                  {activeTab === EMessageTabs.TEXT.toLowerCase() && (
                    <NewMessageText resetMessageInput={resetMessageInput} usersToMention={usersToMention} inline />
                  )}
                  {activeTab === EMessageTabs.CAPSULES.toLowerCase() && (
                    <NewMessageCapsule resetMessageInput={resetMessageInput} usersToMention={usersToMention} inline />
                  )}
                  {activeTab === EMessageTabs.MAGAZINE.toLowerCase() && (
                    <NewMessageMagazine resetMessageInput={resetMessageInput} usersToMention={usersToMention} inline />
                  )}
                  {activeTab === "benefit" && (
                    <NewMessageBenefit resetMessageInput={resetMessageInput} usersToMention={usersToMention} inline />
                  )}
                  {activeTab === EMessageTabs.RECOGNITION.toLowerCase() && (
                    <TabRecognition />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </ExploreCardFrame>
  );
});

export default NewMessage;
